<template>
  <div>
    <loading-overlay v-if="updating || updatingEvents" :text="''" />
    
    <!-- Permission Check: Nur für Admins und Manager (KEIN POS) -->
    <v-container fluid class="px-6 py-6" v-if="!hasGeneralAdminPermission">
      <permission-denied 
        message="Du benötigst Manager- oder Administrator-Rechte, um Events zu verwalten."
      />
    </v-container>
    
    <v-container fluid class="px-6 py-6" v-else-if="event">
      <v-card
        v-if="event && event._id && isOwnerVatMissing"
        class="card-shadow border-radius-xl text-center mt-5 bg-danger mb-10"
      >
        <h5 class="font-weight-bolder text-h5 text-white pt-5 mb-0">
          Umsatzsteuer-ID / Umsatzsteuer Hinweis (Verein,
          Kleinunternehmer-Regelung, ...) fehlt beim Event-Owner! Bitte kontaktiere den Administrator.
        </h5>
        <v-row class="ma-0 pa-0 text-center" align="center" v-if="isAdmin && canArchiveEvent">
          <v-col>
            <v-btn
              elevation="0"
              height="43"
              class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2 mb-sm-0 mb-2"
              color="#5e72e4"
              small
              @click="$router.push('/admin/profile').catch(() => {})"
              >Zum Profil</v-btn
            >
          </v-col>
        </v-row>
      </v-card>

      <v-row class="mb-5">
        <v-col cols="12">
          <!-- Archived Badge -->
          <v-alert
            v-if="event.archived"
            type="warning"
            dense
            colored-border
            border="left"
            elevation="2"
            class="mb-3"
          >
            <div class="d-flex align-center">
              <i class="material-icons-round me-2" style="font-size: 20px; color: #fb6340;">archive</i>
              <div>
                <p class="mb-0 font-weight-600" style="color: #344767;">Event ist archiviert</p>
                <p class="mb-0 text-caption" style="color: #67748e;">
                  Alle Bearbeitungsfunktionen sind deaktiviert.
                </p>
              </div>
            </div>
          </v-alert>

          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-primary py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#5e72e4"
            small
            @click="
              $router.push('/admin/events/details/' + event._id).catch(() => {})
            "
            >Event Details</v-btn
          >

          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-default py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#344767"
            small
            @click="copyEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">content_copy</i>
            Event kopieren
          </v-btn>
          
          <!-- Archivieren / Dearchivieren Button -->
          <v-btn
            v-if="canArchiveEvent && !event.archived"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-warning py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#fb6340"
            small
            @click="archiveEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">archive</i>
            Event archivieren
          </v-btn>
          
          <v-btn
            v-if="canArchiveEvent && event.archived"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-success py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#2dce89"
            small
            @click="unarchiveEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">unarchive</i>
            Event dearchivieren
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="m-0">
        <v-col cols="12" class="pt-0 mb-10 mt-5">
          <v-card class="mb-30 card card-shadow border-radius-xl py-5">
            <div class="mt-n11 mx-4 card-header position-relative z-index-2">
              <div class="d-block blur-shadow-image w-100">
                <img
                  v-if="event.img && !imageError"
                  :src="getImage(event.img)"
                  class="img-fluid shadow border-radius-lg"
                  style="width: 100%; height: 400px; object-fit: cover;"
                  :alt="event.img"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="d-flex align-items-center justify-content-center border-radius-lg shadow"
                  style="
                    width: 100%;
                    height: 400px;
                    background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
                  "
                >
                  <i class="material-icons-round text-white" style="font-size: 120px; opacity: 0.5;">
                    image_not_supported
                  </i>
                </div>
              </div>
              <div
                v-if="event.img && !imageError"
                class="colored-shadow"
                v-bind:style="{
                  backgroundImage: 'url(' + getImage(event.img) + ')',
                }"
              ></div>
            </div>
            <div class="card-header-padding">
              <v-row>
                <v-col>
                  <p class="font-weight-600 text-h3 text-typo mb-0">
                    {{ event.title }}
                  </p>
                  <p v-if="event.lastEditBy && isAdmin" class="text-xs text-secondary mb-0 mt-1">
                    <v-icon x-small class="me-1">edit</v-icon>
                    Zuletzt bearbeitet von
                    <strong>{{ event.lastEditBy.firstname }} {{ event.lastEditBy.lastname }}</strong>
                    <span v-if="event.updated"> · {{ event.updated | moment("DD.MM.YYYY HH:mm") }}</span>
                  </p>
                </v-col>
              </v-row>
            </div>
          </v-card>
          <v-card class="card-shadow border-radius-xl">
            <v-card-text class="card-padding">
              <h5 class="font-weight-bolder text-h5 text-typo mb-0">
                Eventinformationen
              </h5>
              <v-row>
                <v-col cols="12" lg="4" class="mt-5">
                  <h6
                    class="text-caption text-uppercase font-weight-600 text-muted"
                  >
                    Neues Titelbild hochladen (optional)
                  </h6>
                  <dropzone @newFile="setFile" :disabled="formDisabled"></dropzone>
                </v-col>
              </v-row>
              <div class="border-bottom my-5"></div>
              <v-form :class="{ 'form-disabled': formDisabled }">
                <div class="">
                  <v-row>
                    <v-col>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        :class="
                          !event.title || event.title.length < 8
                            ? 'text-danger'
                            : ''
                        "
                        >Eventtitel</label
                      >
                      <v-text-field
                        hide-details
                        outlined
                        v-model="event.title"
                        class="input-style font-size-input text-light-input placeholder-light"
                      >
                      </v-text-field>
                    </v-col>
                    <!-- 
                    <v-col>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        >Eventkürzel (für Codes)</label
                      >
                      <v-text-field
                        type="email"
                        hide-details
                        outlined
                        v-model="event.short"
                        class="
                          input-style
                          font-size-input
                          text-light-input
                          placeholder-light
                        "
                      >
                      </v-text-field>
                    </v-col> -->
                  </v-row>
                  <v-row>
                    <v-col>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        :class="
                          !event.description || event.description.length < 8
                            ? 'text-danger'
                            : ''
                        "
                        >Eventbeschreibung</label
                      >
                      <vue-editor v-model="event.description"></vue-editor>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        >Wichtige Hinweise / AGB (optional)</label
                      >
                      <vue-editor v-model="event.hints"></vue-editor>
                    </v-col>
                  </v-row>

                  

                  <v-row>
                    <v-col cols="12" md="6">
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        :class="
                          !event.slots || event.slots < 10 ? 'text-danger' : ''
                        "
                        >Anzahl Teilnehmer</label
                      >
                      <v-text-field
                        hide-details
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                        type="number"
                        v-model="event.slots"
                      >
                      </v-text-field>

                       <label
                         for=""
                         class="label-color font-weight-600 mb-2 mt-5 d-block"
                       >Platzart / Konzertart (optional)</label>
                       <v-select
                         :items="[
                           { text: 'Keine Auswahl', value: null },
                           { text: 'Open-Air', value: 'Open-Air' },
                           { text: 'Stehplatz', value: 'Stehplatz' },
                           { text: 'Freie Platzwahl', value: 'Freie Platzwahl' },
                           { text: 'Freitext', value: '__custom__' }
                         ]"
                         v-model="seatTypeChoice"
                         dense
                         hide-details
                         outlined
                         placeholder="Bitte wählen"
                         item-text="text"
                         item-value="value"
                         class="font-size-input input-style text-light-input placeholder-light select-style mb-0"
                       >
                       </v-select>
                       <v-text-field
                         v-if="seatTypeChoice === '__custom__'"
                         hide-details
                         outlined
                         class="input-style font-size-input text-light-input placeholder-light mt-3"
                         v-model="seatTypeCustom"
                         placeholder="Eigene Bezeichnung"
                       >
                       </v-text-field>

                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 mt-5 d-block"
                        :class="
                          !event.organizer || event.organizer.length < 4
                            ? 'text-danger'
                            : ''
                        "
                        >Veranstalter</label
                      >
                      <v-text-field
                        hide-details
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                        v-model="event.organizer"
                      >
                      </v-text-field>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 mt-5 d-block"
                        >Website Veranstalter (optional)</label
                      >
                      <v-text-field
                        hide-details
                        outlined
                        class="input-style font-size-input text-light-input placeholder-light"
                        v-model="event.website"
                      >
                      </v-text-field>
                    </v-col>
                    <v-col>
                      <v-card
                        class="pa-4 mt-0 mt-md-7"
                        :class="
                          event.hide_ticket_amount
                            ? 'bg-gradient-success'
                            : 'bg-gradient-secondary'
                        "
                      >
                        <div class="d-flex">
                          <div>
                            <p
                              class="mb-0 text-h5 font-weight-light text-white"
                            >
                              Verbleibende Tickets verbergen
                            </p>
                            <p class="mb-0 font-weight-light text-white">
                              Verbleibende Tickets werden
                              {{ event.hide_ticket_amount ? "nicht" : "" }}
                              angezeigt.
                            </p>
                          </div>

                          <v-switch
                            v-model="event.hide_ticket_amount"
                            :ripple="true"
                            class="d-inline-flex mt-0 pt-0 switch ms-auto"
                          ></v-switch>
                        </div>
                      </v-card>
                      <v-card
                        class="pa-4 mt-0 mt-2"
                        :class="
                          event.personalization_required
                            ? 'bg-gradient-success'
                            : 'bg-gradient-secondary'
                        "
                      >
                        <div class="d-flex">
                          <div>
                            <p
                              class="mb-0 text-h5 font-weight-light text-white"
                            >
                              Personalisierung erzwingen
                            </p>
                            <p class="mb-0 font-weight-light text-white">
                              Tickets müssen
                              {{
                                event.personalization_required ? "" : "nicht"
                              }}
                              personalisiert werden.
                            </p>
                          </div>

                          <v-switch
                            v-model="event.personalization_required"
                            :ripple="true"
                            class="d-inline-flex mt-0 pt-0 switch ms-auto"
                          ></v-switch>
                        </div>
                      </v-card>
                      <v-card
                        class="pa-4 mt-2"
                        :class="
                          event.address_required
                            ? 'bg-gradient-success'
                            : 'bg-gradient-secondary'
                        "
                      >
                        <div class="d-flex">
                          <div>
                            <p
                              class="mb-0 text-h5 font-weight-light text-white"
                            >
                              Adressdaten anfordern
                            </p>
                            <p class="mb-0 font-weight-light text-white">
                              Adressangaben müssen
                              {{ event.address_required ? "" : "nicht" }}
                              personalisiert werden.
                            </p>
                          </div>

                          <v-switch
                            v-model="event.address_required"
                            :ripple="true"
                            class="d-inline-flex mt-0 pt-0 switch ms-auto"
                          ></v-switch>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                  <h5 class="font-weight-bolder text-h5 text-typo mb-0 mt-5">
                    Einlass / Beginn
                  </h5>
                  <v-row>
                    <v-col cols="12" sm="6" xl="4">
                      <v-card class="pa-4 mt-2 bg-gradient-light">
                        <v-row justify="center" class="mt-1 mb-3">
                          <div>
                            <div>
                              <label
                                for="datepicker"
                                class="text-h6"
                                :class="!event.date_inlet ? 'text-danger' : ''"
                                >Angezeigtes Datum für den Einlass</label
                              >
                            </div>
                          </div>
                        </v-row>
                        <v-row justify="center" class="mb-4">
                          <date-picker
                            id="entry"
                            v-model="event.date_inlet"
                            mode="dateTime"
                            is24hr
                            :min-date="new Date()"
                          />
                        </v-row>
                        <v-row
                          justify="center"
                          class="mt-1 mb-3"
                          v-if="!event.date_inlet"
                        >
                          <div>
                            <div>
                              <label
                                for="datepicker"
                                class="text-h6 text-danger"
                                >Einlasszeit wird benötigt!</label
                              >
                            </div>
                          </div>
                        </v-row>
                      </v-card>
                    </v-col>

                    <v-col cols="12" sm="6" xl="4">
                      <v-card class="pa-4 mt-2 bg-gradient-light">
                        <v-row justify="center" class="mt-1 mb-3">
                          <div>
                            <div>
                              <label
                                for="datepicker"
                                class="text-h6"
                                :class="!event.date ? 'text-danger' : ''"
                                >Angezeigtes Datum für den Beginn</label
                              >
                            </div>
                          </div>
                        </v-row>
                        <v-row justify="center" class="mb-4">
                          <date-picker
                            id="entry"
                            v-model="event.date"
                            mode="dateTime"
                            is24hr
                            :min-date="new Date()"
                          />
                        </v-row>
                        <v-row
                          justify="center"
                          class="mt-1 mb-3"
                          v-if="!event.date"
                        >
                          <div>
                            <div>
                              <label
                                for="datepicker"
                                class="text-h6 text-danger"
                                >Beginn wird benötigt!</label
                              >
                            </div>
                          </div>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                  <h5 class="font-weight-bolder text-h5 text-typo mb-0 my-5">
                    Sichtbarkeit / Anzeigedatum
                  </h5>
                  <v-row>
                    <v-col cols="12" sm="6" xl="4">
                      <v-card class="pa-4 mt-2 bg-gradient-light">
                        <v-row justify="center" class="mt-1 mb-3">
                          <label
                            for="start"
                            class="text-h6"
                            :class="!event.date_start ? 'text-danger' : ''"
                            >Event wird angezeigt ab:</label
                          >
                        </v-row>
                        <v-row justify="center" class="mb-4">
                          <date-picker
                            id="start"
                            v-model="event.date_start"
                            mode="dateTime"
                            is24hr
                            :min-date="new Date()"
                          />
                        </v-row>
                        <v-row
                          justify="center"
                          class="mt-1 mb-3"
                          v-if="!event.date_start"
                        >
                          <div>
                            <div>
                              <label
                                for="datepicker"
                                class="text-h6 text-danger"
                                >Anzeige ab wird benötigt!</label
                              >
                            </div>
                          </div>
                        </v-row>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" xl="4">
                      <v-card class="pa-4 mt-2 bg-gradient-light">
                        <v-row justify="center" class="mt-1 mb-3">
                          <label
                            for="end"
                            class="text-h6"
                            :class="!event.date_end ? 'text-danger' : ''"
                            >Event wird angezeigt bis:</label
                          >
                        </v-row>
                        <v-row justify="center" class="mb-4">
                          <date-picker
                            id="end"
                            v-model="event.date_end"
                            mode="dateTime"
                            is24hr
                            :min-date="new Date()"
                          />
                        </v-row>
                        <v-row
                          justify="center"
                          class="mt-1 mb-3"
                          v-if="!event.date_end"
                        >
                          <div>
                            <div>
                              <label
                                for="datepicker"
                                class="text-h6 text-danger"
                                >Anzeige bis wird benötigt!</label
                              >
                            </div>
                          </div>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <label
                        for=""
                        class="label-color font-weight-600 text-h6 mb-2 d-block"
                        >Verbundenes Event (Optional)
                      </label>
                      <div class="d-flex align-center">
                        <v-autocomplete
                          :items="[
                            { _id: null, title: '- kein Event verbinden -' },
                            ...eventsForDropdown.filter((el) => el._id != event._id),
                          ]"
                          v-model="event.linkedEvent"
                          :loading="loadingEventsForDropdown"
                          :disabled="event.archived || loadingEventsForDropdown"
                          hide-details
                          outlined
                          placeholder="Event wählen (optional)"
                          item-value="_id"
                          item-text="title"
                          class="font-size-input input-style text-light-input placeholder-light select-style mb-0 event-search-autocomplete"
                          single-line
                          height="28"
                          clearable
                          :search-input.sync="linkedEventSearch"
                          no-data-text="Keine Events gefunden"
                        >
                          <template v-slot:prepend-inner>
                            <i class="material-icons-round" style="font-size: 18px; color: #67748e;">search</i>
                          </template>
                        </v-autocomplete>
                        <v-btn
                          @click="loadEventsForDropdown"
                          :loading="loadingEventsForDropdown"
                          :disabled="event.archived || loadingEventsForDropdown"
                          icon
                          small
                          class="ml-2"
                          color="primary"
                          title="Events neu laden"
                        >
                          <i class="material-icons-round" style="font-size: 20px;">refresh</i>
                        </v-btn>
                      </div>
                      <p class="mt-5 text-h6">
                        Information zur Verbindung von Events:
                      </p>
                      <p class="mt-2">
                        Wird ein weiteres Event mit diesem verbunden, so ist der
                        Ticket-Scan für das VERBUNDENE Event, auch bei der
                        Auswahl DIESES Events, innerhalb der App möglich.
                      </p>
                      <p class="mt-2">
                        Hinweis: dies funktioniert nur in bei der Auswahl DIESES
                        Events innerhalb der App. Soll das andere Event auch
                        verbunden werden, muss dies dort separat gewählt werden.
                      </p>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
          <v-card class="card-shadow border-radius-xl mt-5">
            <v-card-text class="card-padding">
              <h5 class="font-weight-bolder text-h5 text-typo mb-0">
                Veranstaltungsort
              </h5>
              <div class="mt-5">
                <v-row>
                  <v-col>
                    <label
                      for=""
                      class="label-color font-weight-600 mb-2 d-block"
                      :class="
                        !event.street || event.street.length < 4
                          ? 'text-danger'
                          : ''
                      "
                      >Straße</label
                    >
                    <v-text-field
                      hide-details
                      outlined
                      class="input-style font-size-input text-light-input placeholder-light"
                      v-model="event.street"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <label
                      for=""
                      class="label-color font-weight-600 mb-2 d-block"
                      :class="
                        !event.zipcode || event.zipcode.length < 5
                          ? 'text-danger'
                          : ''
                      "
                      >Postleitzahl</label
                    >
                    <v-text-field
                      type="number"
                      hide-details
                      outlined
                      class="input-style font-size-input text-light-input placeholder-light"
                      v-model="event.zipcode"
                    >
                    </v-text-field>
                  </v-col>

                  <v-col>
                    <label
                      for=""
                      class="label-color font-weight-600 mb-2 d-block"
                      :class="
                        !event.city || event.city.length < 3
                          ? 'text-danger'
                          : ''
                      "
                      >Ort</label
                    >
                    <v-text-field
                      hide-details
                      outlined
                      class="input-style font-size-input text-light-input placeholder-light"
                      v-model="event.city"
                    >
                    </v-text-field>
                  </v-col>

                  <v-col>
                    <label
                      for=""
                      class="label-color font-weight-600 mb-2 d-block"
                      >Land</label
                    >
                    <v-text-field
                      hide-details
                      outlined
                      disabled
                      class="input-style font-size-input text-light-input placeholder-light"
                      placeholder="Deutschland"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <label
                      for=""
                      class="label-color font-weight-600 mb-2 d-block"
                      :class="
                        !event.venue || event.venue.length < 4
                          ? 'text-danger'
                          : ''
                      "
                      >Veranstaltungsstätte</label
                    >
                    <v-text-field
                      hide-details
                      outlined
                      class="input-style font-size-input text-light-input placeholder-light"
                      v-model="event.venue"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>

          <v-card class="card-shadow border-radius-xl mt-5">
            <v-card-text class="card-padding">
              <h5 class="font-weight-bolder text-h5 text-typo mb-0">
                Rechnungsangaben
              </h5>

              <div class="mt-5">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-row>
                      <v-col cols="12">
                        <v-card class="h-100">
                          <!-- <v-card
                            class="px-4 pt-4"
                            :class="
                              freeEvent
                                ? 'bg-gradient-success'
                                : 'bg-gradient-secondary'
                            "
                          >
                            <div class="d-flex">
                              <div>
                                <p
                                  class="mb-0 text-h5 font-weight-light text-white"
                                >
                                  Gratisevent
                                </p>
                              </div>

                              <v-switch
                                v-model="freeEvent"
                                :ripple="true"
                                class="d-inline-flex mt-0 pt-0 switch ms-auto"
                              ></v-switch>
                            </div>
                          </v-card> -->

                          <div v-if="!freeEvent">
                            <v-col cols="12">
                              <label
                                for=""
                                class="label-color font-weight-600 mb-2 d-block"
                                >Onlinepreis in €</label
                              >
                              <v-text-field
                                hide-details
                                outlined
                                type="number"
                                class="input-style font-size-input text-light-input placeholder-light"
                                placeholder="12.34"
                                v-model="event.price"
                              >
                              </v-text-field>
                            </v-col>
                            <v-col cols="12">
                              <label
                                for=""
                                class="label-color font-weight-600 mb-2 d-block"
                                >Eintrittspreis Ermäßigt in € (0.00€ = keine
                                Ermäßigung)</label
                              >
                              <v-text-field
                                hide-details
                                outlined
                                type="number"
                                class="input-style font-size-input text-light-input placeholder-light"
                                placeholder="12.34"
                                v-model="event.price_lowered"
                              >
                              </v-text-field>
                            </v-col>
                            <v-col cols="12">
                              <label
                                for=""
                                class="label-color font-weight-600 mb-2 d-block"
                                >Preis Abendkasse in € (0.00€ = Gratis)</label
                              >
                              <v-text-field
                                hide-details
                                outlined
                                type="number"
                                class="input-style font-size-input text-light-input placeholder-light"
                                placeholder="12.34"
                                v-model="event.price_local"
                              >
                              </v-text-field>
                            </v-col>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col>
                    <v-row>
                      <v-col cols="12">
                        <label
                          for=""
                          class="label-color font-weight-600 mb-2 d-block"
                          >Ermäßigungshinweise</label
                        >
                        <v-textarea
                          hide-details
                          class="input-style font-size-input text-light-input placeholder-light"
                          dense
                          flat
                          outlined
                          height="200"
                          name="input-7-4"
                          style="
                            border-color: rgba(255, 255, 255) !important;
                            border-width: thick !important;
                          "
                          v-model="event.price_lowered_info"
                          placeholder="Ermäßigungshinweise"
                        ></v-textarea>
                      </v-col>
                      <v-col>
                        <label
                          for=""
                          class="label-color font-weight-600 mb-2 d-block"
                          >Rechnungs-Prefix</label
                        >
                        <v-text-field
                          hide-details
                          outlined
                          class="input-style font-size-input text-light-input placeholder-light"
                          v-model="event.invoicePrefix"
                        >
                        </v-text-field>
                      </v-col>
                      <v-col v-if="eventOwnerInfo && !isOwnerVatMissing && ownerVatInfo.vatNumber">
                        <label
                          for=""
                          class="label-color font-weight-600 mb-2 d-block"
                          >Umsatzsteuer in %</label
                        >
                        <v-text-field
                          hide-details
                          outlined
                          class="input-style font-size-input text-light-input placeholder-light"
                          v-model="event.vat_value"
                        >
                        </v-text-field>
                      </v-col>
                      <v-col v-else>
                        <label
                          for=""
                          class="label-color font-weight-600 mb-2 d-block"
                          >Umsatzsteuer in %</label
                        >
                        <p v-if="eventOwnerInfo && !isOwnerVatMissing && ownerVatInfo.vatMessage">
                          {{ ownerVatInfo.vatMessage }}
                        </p>
                        <p class="text-danger" v-else-if="eventOwnerInfo">
                          Umsatzsteuer-ID / Umsatzsteuer Hinweis (Verein,
                          Kleinunternehmer-Regelung, ...) fehlt beim Event-Owner!
                        </p>
                        <p v-else class="text-muted">
                          Lade Owner-Informationen...
                        </p>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>

          <v-card
            v-if="event && event._id && isOwnerVatMissing"
            class="card-shadow border-radius-xl text-center mt-5 bg-danger mb-10"
          >
            <h5 class="font-weight-bolder text-h5 text-white pt-5 mb-0">
              Umsatzsteuer-ID / Umsatzsteuer Hinweis (Verein,
              Kleinunternehmer-Regelung, ...) fehlt beim Event-Owner! Bitte kontaktiere den Administrator.
            </h5>
            <v-row class="ma-0 pa-0 text-center" align="center" v-if="isAdmin && canArchiveEvent">
              <v-col>
                <v-btn
                  elevation="0"
                  height="43"
                  class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2 mb-sm-0 mb-2"
                  color="#5e72e4"
                  small
                  @click="$router.push('/admin/profile').catch(() => {})"
                  >Zum Profil</v-btn
                >
              </v-col>
            </v-row>
          </v-card>

          <v-row class="pa-0 ma-0 mt-10">
            <v-col>
              <v-row align="center">
                <v-btn
                  elevation="0"
                  :ripple="false"
                  :disabled="checkForm || event.archived"
                  height="43"
                  @click="postEventTest()"
                  class="font-weight-600 text-capitalize bg-gradient-success text-white py-3 mr-3 mt-3 px-6 rounded-sm"
                  :color="
                    !checkForm && !event.archived ? 'bg-gradient-success' : 'bg-gradient-light'
                  "
                  >Event speichern</v-btn
                >
                <v-btn
                  elevation="0"
                  :ripple="false"
                  height="43"
                  :disabled="event.archived"
                  @click="loadEventsForDropdown(); $store.dispatch('getEventByIdAdmin', event._id);"
                  class="font-weight-600 text-capitalize text-white bg-gradient-danger py-3 px-6 mt-3 rounded-sm"
                  color="#fff"
                  >Änderungen zurücksetzen</v-btn
                >
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-snackbar
        top
        v-model="snackbar.visible"
        :color="snackbar.color"
        class="snackbar-shadow"
      >
        <div class="d-flex align-start alert-notify">
          <v-icon size="24" class="text-white me-3 mt-1 material-icons-round"
            >notifications</v-icon
          >
          <p class="mb-0">
            <span class="font-size-root font-weight-600">{{
              snackbar.title
            }}</span>
            <br />
            {{ snackbar.message }}
          </p>
        </div>

        <template v-slot:action="{ attrs }">
          <v-btn
            icon
            elevation="0"
            max-width="136"
            :ripple="false"
            height="43"
            class="font-weight-600 text-capitalize py-3 px-6 rounded-sm"
            color="rgba(255,255,255, .85)"
            @click="snackbar.visible = false"
            v-bind="attrs"
          >
            <v-icon size="13">fas fa-times</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>

    <!-- Archive Confirmation Dialog -->
    <v-dialog v-model="dialogArchive" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-warning me-2" style="font-size: 28px;">archive</i>
          Event archivieren?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Bist Du sicher, dass Du dieses Event archivieren möchtest?
          </p>
          
          <v-alert type="warning" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Das Event wird archiviert und automatisch deaktiviert:</p>
            <ul class="pl-4 text-typo">
              <li>Keine neuen Anmeldungen mehr möglich</li>
              <li>Event ist nicht mehr öffentlich sichtbar</li>
              <li>Änderungen sind nicht mehr möglich</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeArchiveDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-warning py-2 px-6"
            color="#fb6340"
            @click="confirmArchive"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">archive</i>
            Jetzt archivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unarchive Confirmation Dialog -->
    <v-dialog v-model="dialogUnarchive" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-success me-2" style="font-size: 28px;">unarchive</i>
          Event dearchivieren?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Möchtest Du dieses Event wirklich dearchivieren?
          </p>
          
          <v-alert type="info" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Nach der Dearchivierung:</p>
            <ul class="pl-4 text-typo">
              <li>Event bleibt zunächst inaktiv</li>
              <li>Event und Anmeldungen müssen manuell aktiviert werden</li>
              <li>Bearbeitung ist wieder möglich</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeUnarchiveDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-success py-2 px-6"
            color="#2dce89"
            @click="confirmUnarchive"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">unarchive</i>
            Dearchivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Merge-Konflikt Dialog -->
    <event-conflict-dialog
      :open="conflictDialogOpen"
      :conflictData="conflictData"
      :localData="conflictLocalData"
      @close="handleConflictClose"
      @use-server-version="handleConflictUseServer"
      @force-my-version="handleConflictForce"
    />
  </div>
</template>
<script>
// import CalendarWidget from "./Widgets/CalendarWidget.vue";
import { VueEditor } from "vue2-editor";

import DatePicker from "v-calendar/lib/components/date-picker.umd";
import Dropzone from "./Widgets/Dropzone.vue";
import coreData from "@/mixins/coreDataMixin";
import permissionMixin from "@/mixins/permissionMixin";
import PermissionDenied from "@/components/PermissionDenied.vue";
import EventConflictDialog from "./Widgets/EventConflictDialog.vue";
import axiosAuth from "@/shared/configs/axios-auth";
import { mapGetters } from 'vuex';

export default {
  name: "EventEdit",
  mixins: [coreData, permissionMixin],
  components: {
    VueEditor,
    DatePicker,
    Dropzone,
    PermissionDenied,
    EventConflictDialog,
  },
  data: function () {
    return {
      freeEvent: false,
      seatTypeForceCustom: false,
      imageError: false,
      dialogArchive: false,
      dialogUnarchive: false,
      loadingEventsForDropdown: false,
      eventsForDropdown: [],
      linkedEventSearch: null,
      eventOwnerInfo: null, // Stores the actual event owner's data
      conflictDialogOpen: false,
      conflictData: null,
      conflictLocalData: null,
    };
  },
  async mounted() {
    // Berechtigungsprüfung: Nur Owner oder Manager mit canManage (KEIN POS)
    const isAdmin = this.$store.getters.isAdmin;
    const isManager = this.$store.getters.isManager;
    
    if (!isAdmin && !isManager) {
      console.log('🚫 [EVENT EDIT] No permission - redirecting to events list');
      this.$router.push('/admin/events').catch(() => {});
      return;
    }
    
    // Direkter Aufruf: Event per ID laden
    const eventId = this.$route.params.id;
    if (eventId) {
      try {
        // IMMER vom Backend laden (auch wenn im Store), um vollständige Daten zu haben
        console.log('🔄 [EVENT EDIT] Loading event by ID:', eventId);
        await this.$store.dispatch("getEventByIdAdmin", eventId);
        console.log('✅ [EVENT EDIT] Event loaded from backend');
        
        // Zusätzliche Prüfung: Ist der User Owner oder hat canManage für dieses Event?
        const event = this.$store.getters.events.find(e => e._id === eventId);
        if (event) {
          const eventManagerId = event.manager?._id || event.manager;
          const ownManagerId = this.$store.getters.ownManagerId;
          const isOwner = eventManagerId && ownManagerId && eventManagerId.toString() === ownManagerId.toString();
          
          if (!isOwner && !isAdmin) {
            // Prüfe ob User canManage Berechtigung für den Event-Manager hat
            const canManage = this.$store.getters.canManageEvents(eventManagerId);
            if (!canManage) {
              console.log('🚫 [EVENT EDIT] No canManage permission for this event - redirecting');
              this.$router.push('/admin/events').catch(() => {});
              return;
            }
          }
        }
      } catch (error) {
        console.error('❌ [EVENT EDIT] Fehler beim Laden des Events:', error);
        // Bei 403 (Forbidden) zur Events-Liste umleiten
        if (error.response && error.response.status === 403) {
          this.$router.push('/admin/events').catch(() => {});
          return;
        }
      }
    }
    
    // Events für Dropdown laden (optimierte Route)
    this.loadEventsForDropdown();
    
    // loadEventOwnerInfo wird im event-Watcher aufgerufen (mit immediate: true)
  },
  computed: {
    seatTypeChoice: {
      get() {
        const fixed = ["Open-Air", "Stehplatz", "Freie Platzwahl"]
        const val = this.event && this.event.seat_type
        if (this.seatTypeForceCustom) return '__custom__'
        if (!val) return null
        return fixed.includes(val) ? val : '__custom__'
      },
      set(value) {
        if (value === '__custom__') {
          this.seatTypeForceCustom = true
          return
        }
        this.seatTypeForceCustom = false
        if (this.event) this.$set(this.event, 'seat_type', value || '')
      }
    },
    seatTypeCustom: {
      get() {
        const fixed = ["Open-Air", "Stehplatz", "Freie Platzwahl"]
        const val = this.event && this.event.seat_type
        if (!val || fixed.includes(val)) return ''
        return val
      },
      set(value) {
        if (!this.event) return
        if (this.seatTypeChoice === '__custom__') {
          this.$set(this.event, 'seat_type', (value || '').trim())
        }
      }
    },
    checkForm() {
      // If no event, form is invalid
      if (!this.event || !this.event._id) {
        console.log('📋 [EVENT EDIT] checkForm: No event loaded yet');
        return true; // Form is INVALID
      }
      
      // If eventOwnerInfo not loaded yet, form is invalid (wait for VAT info)
      if (!this.eventOwnerInfo) {
        console.log('📋 [EVENT EDIT] checkForm: Owner info not loaded yet');
        return true; // Form is INVALID
      }
      
      const fields = {
        title: !!this.event.title,
        description: !!this.event.description,
        date: !!this.event.date,
        date_start: !!this.event.date_start,
        date_end: !!this.event.date_end,
        organizer: !!this.event.organizer,
        price: this.event.price !== null && this.event.price !== undefined,
        street: !!this.event.street,
        city: !!this.event.city,
        zipcode: !!this.event.zipcode,
        slots: !!this.event.slots,
        invoicePrefix: !!this.event.invoicePrefix,
        venue: !!this.event.venue,
        date_inlet: !!this.event.date_inlet,
        vatOk: !this.isOwnerVatMissing
      };
      
      const formValid = fields.title &&
        fields.description &&
        fields.date &&
        fields.date_start &&
        fields.date_end &&
        fields.organizer &&
        fields.price &&
        fields.street &&
        fields.city &&
        fields.zipcode &&
        fields.slots &&
        fields.invoicePrefix &&
        fields.venue &&
        fields.date_inlet &&
        fields.vatOk;
      
      console.log('📋 [EVENT EDIT] checkForm - Missing fields:', 
        Object.entries(fields).filter(([key, value]) => !value).map(([key]) => key),
        fields
      );
      
      return !formValid; // Return true if form is INVALID
    },
    
    ...mapGetters(['isAdmin', 'ownManagerId']),
    
    // Use event owner's VAT info, fallback to logged-in manager
    ownerVatInfo() {
      const result = this.eventOwnerInfo || this.userInformations;
      console.log('🔍 [EVENT EDIT] ownerVatInfo computed:', {
        hasEventOwnerInfo: !!this.eventOwnerInfo,
        hasUserInformations: !!this.userInformations,
        vatNumber: result?.vatNumber,
        vatMessage: result?.vatMessage
      });
      return result;
    },
    
    // Separate computed property for VAT missing check (better reactivity)
    isOwnerVatMissing() {
      // If eventOwnerInfo not loaded yet, don't show warning (return false)
      if (!this.eventOwnerInfo) {
        console.log('🚨 [EVENT EDIT] isOwnerVatMissing: eventOwnerInfo not loaded yet');
        return false; // Don't show warning until we have data
      }
      
      const info = this.ownerVatInfo;
      const missing = !info || (!info.vatNumber && !info.vatMessage);
      console.log('🚨 [EVENT EDIT] isOwnerVatMissing:', missing, {
        hasInfo: !!info,
        vatNumber: info?.vatNumber,
        vatMessage: info?.vatMessage
      });
      return missing;
    },
    
    canArchiveEvent() {
      // Nur Event-Owner (admin: true) können ihr eigenes Event archivieren
      // NICHT eingeladene Manager (POS/Checker)
      if (!this.event || !this.isAdmin) return false;
      
      // Prüfe ob es das eigene Event ist (Owner)
      const eventManagerId = this.event.manager._id || this.event.manager;
      return this.ownManagerId === eventManagerId;
    },
    
    formDisabled() {
      return this.event && this.event.archived;
    }
  },
  watch: {
    event: {
      handler(newEvent) {
        if (newEvent && newEvent._id && newEvent.manager) {
          console.log("event is there", newEvent._id, "manager:", newEvent.manager._id || newEvent.manager);
          if (
            newEvent.price == 0 &&
            newEvent.price_lowered == 0 &&
            newEvent.price_local == 0
          ) {
            console.log("event is there and change");
            this.freeEvent = true;
          }
          // Reset image error when event changes
          this.imageError = false;
          // Load event owner info (only if manager is available)
          this.loadEventOwnerInfo();
        } else if (newEvent && newEvent._id) {
          console.log("⚠️ [EVENT EDIT] Event loaded but manager not populated yet:", newEvent._id);
        }
      },
      immediate: true, // Trigger auch beim ersten Render
    },
    'event.img'() {
      // Reset error state when image changes
      this.imageError = false;
    },
    '$route.params.id': {
      async handler(newId, oldId) {
        if (newId && newId !== oldId) {
          console.log(`[EVENT EDIT] Route changed from ${oldId} to ${newId}`);
          // Event neu laden
          await this.$store.dispatch("getEventByIdAdmin", newId);
          // Events für Dropdown neu laden
          this.loadEventsForDropdown();
        }
      }
    },
    freeEvent() {
      if (this.freeEvent) {
        this.event.price = 0;
        this.event.price_lowered = 0;
        this.event.price_local = 0;
      } else {
      }
    },
  },
  methods: {
    copyEvent() {
      if (!this.event) return;
      // Sanitize event data for copying: exclude process-relevant fields
      const copyData = {
        title: '[KOPIE] ' + (this.event.title || ''),
        description: this.event.description,
        hints: this.event.hints,
        disclaimer: this.event.disclaimer,
        html: this.event.html,
        short: this.event.short,
        organizer: this.event.organizer,
        slots: this.event.slots,
        street: this.event.street,
        venue: this.event.venue,
        city: this.event.city,
        zipcode: this.event.zipcode,
        price: this.event.price,
        price_lowered: this.event.price_lowered,
        price_lowered_info: this.event.price_lowered_info,
        price_local: this.event.price_local,
        vat_value: this.event.vat_value,
        website: this.event.website,
        date: this.event.date ? new Date(this.event.date) : null,
        date_start: this.event.date_start ? new Date(this.event.date_start) : null,
        date_end: this.event.date_end ? new Date(this.event.date_end) : null,
        date_inlet: this.event.date_inlet ? new Date(this.event.date_inlet) : null,
        hide_ticket_amount: this.event.hide_ticket_amount,
        personalization_required: this.event.personalization_required,
        address_required: this.event.address_required,
        seat_type: this.event.seat_type,
      };
      this.$store.commit('setEventCopyData', copyData);
      this.$router.push('/admin/events/new/').catch(() => {});
    },
    async loadEventOwnerInfo() {
      if (!this.event || !this.event.manager) {
        console.log('⚠️ [EVENT EDIT] No event or event.manager available yet');
        return;
      }
      
      const eventManagerId = this.event.manager._id || this.event.manager;
      console.log(`📊 [EVENT EDIT] Loading owner info for manager: ${eventManagerId}`);
      
      try {
        const response = await axiosAuth.get(`/authnews/manager/${eventManagerId}`);
        // Use $set to ensure Vue reactivity
        this.$set(this.$data, 'eventOwnerInfo', response.data);
        console.log('✅ [EVENT EDIT] Event owner info loaded:', this.eventOwnerInfo);
        
        // Force computed property re-evaluation
        await this.$nextTick();
        console.log('🔄 [EVENT EDIT] After nextTick, ownerVatInfo should be updated');
      } catch (error) {
        console.error('❌ [EVENT EDIT] Error loading event owner info:', error);
        // Fallback to logged-in user if owner can't be loaded
        this.$set(this.$data, 'eventOwnerInfo', this.userInformations);
      }
    },
    handleImageError() {
      this.imageError = true;
    },
    async loadEventsForDropdown() {
      this.loadingEventsForDropdown = true;
      try {
        const response = await axiosAuth.get('/events/admin/list', {
          params: { includeArchived: false }
        });
        this.eventsForDropdown = response.data || [];
        console.log(`Loaded ${this.eventsForDropdown.length} events for dropdown`);
      } catch (error) {
        console.error('Error loading events for dropdown:', error);
        this.SnackbarShow('Danger', 'Fehler!', 'Events konnten nicht geladen werden.');
        this.eventsForDropdown = [];
      } finally {
        this.loadingEventsForDropdown = false;
      }
    },
    setFile(file) {
      console.log("file emitted!");
      this.event.file = file;
      // Reset image error when new file is uploaded
      this.imageError = false;
    },
    handleConflictClose() {
      this.conflictDialogOpen = false;
      this.conflictData = null;
      this.conflictLocalData = null;
    },
    async handleConflictUseServer() {
      this.conflictDialogOpen = false;
      await this.$store.dispatch("getEventByIdAdmin", this.event._id);
      this.SnackbarShow("Info", "Server-Version geladen", "Die aktuelle Server-Version wurde übernommen.");
      this.conflictData = null;
      this.conflictLocalData = null;
    },
    async handleConflictForce() {
      this.conflictDialogOpen = false;
      await this.postEventTest(true);
      this.conflictData = null;
      this.conflictLocalData = null;
    },
    
    archiveEvent() {
      this.dialogArchive = true;
    },

    async confirmArchive() {
      this.dialogArchive = false;

      try {
        this.$store.commit("updating", true);

        // PATCH Request mit archived: true (Backend setzt automatisch active: false und entries_active: false)
        await axiosAuth.patch(`/events/${this.event._id}`, {
          archived: true
        });

        this.SnackbarShow("Success", "Event archiviert!", "Das Event wurde archiviert und deaktiviert.");
        
        // Zurück zur Events-Liste
        this.$router.push('/admin/events');
      } catch (error) {
        console.error('Archive event error:', error);
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Archivieren!';
        this.SnackbarShow("Danger", "Fehler", message);
      } finally {
        this.$store.commit("updating", false);
      }
    },

    closeArchiveDialog() {
      this.dialogArchive = false;
    },

    unarchiveEvent() {
      this.dialogUnarchive = true;
    },

    async confirmUnarchive() {
      this.dialogUnarchive = false;

      try {
        this.$store.commit("updating", true);

        // PATCH Request mit archived: false
        await axiosAuth.patch(`/events/${this.event._id}`, {
          archived: false,
          archivedAt: null
        });

        this.SnackbarShow("Success", "Event dearchiviert!", "Das Event ist nun wieder bearbeitbar.");
        
        // Events neu laden
        await this.$store.dispatch("getEventByIdAdmin", this.event._id);
        await this.loadEventsForDropdown();
      } catch (error) {
        console.error('Unarchive event error:', error);
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Dearchivieren!';
        this.SnackbarShow("Danger", "Fehler", message);
      } finally {
        this.$store.commit("updating", false);
      }
    },

    closeUnarchiveDialog() {
      this.dialogUnarchive = false;
    },

    async postEventTest(forceOverwrite) {
      if (!this.event.title || !this.event.description) return;
      this.$store.commit("updating", true);
      try {
        let formData = new FormData();
        formData.append("description", this.event.description);
        formData.append("title", this.event.title);
        formData.append("disclaimer", this.event.disclaimer);
        formData.append("organizer", this.event.organizer);
        formData.append("date", new Date(this.event.date));
        formData.append("linkedEvent", this.event.linkedEvent);

        if (this.event.date_start) {
          formData.append("date_start", new Date(this.event.date_start));
        }
        if (this.event.date_end) {
          formData.append("date_end", new Date(this.event.date_end));
        }
        if (this.event.date_inlet) {
          formData.append("date_inlet", new Date(this.event.date_inlet));
        }

        if (this.event.hide_ticket_amount) {
          formData.append("hide_ticket_amount", true);
        } else {
          formData.append("hide_ticket_amount", false);
        }

        if (this.event.personalization_required) {
          formData.append("personalization_required", true);
        } else {
          formData.append("personalization_required", false);
        }

        if (this.event.address_required) {
          formData.append("address_required", true);
        } else {
          formData.append("address_required", false);
        }

        formData.append("slots", this.event.slots);
        formData.append("hints", this.event.hints);
        formData.append("price", this.event.price);
        formData.append("price_lowered", this.event.price_lowered);
        formData.append("price_lowered_info", this.event.price_lowered_info);
        formData.append("price_local", this.event.price_local);
        if (this.event.vat_value) {
          formData.append("vat_value", this.event.vat_value);
        }

        formData.append("html", this.event.html);
        formData.append("short", this.event.short);
        // Manager and client_id are not sent - they must not be changed during edit
        // Backend will ignore these fields anyway (delete req.body.manager)
        formData.append("street", this.event.street);
        formData.append("city", this.event.city);
        if (this.event.venue) formData.append("venue", this.event.venue);
        formData.append("zipcode", this.event.zipcode);
        formData.append("invoicePrefix", this.event.invoicePrefix);
        formData.append("website", this.event.website);
        // Compute seat_type from selector/custom
        let computedSeatType = ''
        if (this.seatTypeChoice === '__custom__') {
          computedSeatType = (this.seatTypeCustom || '').trim()
        } else if (this.seatTypeChoice) {
          computedSeatType = this.seatTypeChoice
        }
        formData.append('seat_type', computedSeatType)

        if (this.event.file) {
          formData.append("image", this.event.file);
        } else {
          formData.append("img", this.event.img);
        }

        // Conflict-Check: Send last known update timestamp (skip on force overwrite)
        if (!forceOverwrite && this.event.updated) {
          formData.append("_lastKnownUpdate", this.event.updated);
        }

        await axiosAuth.patch("events/" + this.event._id, formData);
        this.uploadComplete = true;
        this.$store.commit("updating", false);
        await this.$store.dispatch("getEventByIdAdmin", this.event._id);
        this.loadEventsForDropdown();
        this.SnackbarShow(
          "Success",
          "Aktion erfolgreich!",
          "Event gespeichert!"
        );
      } catch (error) {
        this.$store.commit("updating", false);
        // Handle 409 Conflict (merge conflict detection)
        if (error.response && error.response.status === 409 && error.response.data && error.response.data.conflict) {
          this.conflictData = error.response.data;
          this.conflictLocalData = { ...this.event };
          this.conflictDialogOpen = true;
        } else {
          console.error('Error updating event:', error);
          this.SnackbarShow(
            "Danger",
            "Fehler!",
            "Etwas ist schief gelaufen..."
          );
        }
      }
    },
  },
};
</script>

<style scoped>
/* Disable form when event is archived */
.form-disabled {
  pointer-events: none;
  opacity: 0.6;
}

/* Fix search icon position in autocomplete */
.event-search-autocomplete >>> .v-input__prepend-inner {
  margin-top: 6px !important;
  padding-top: 0 !important;
  align-self: center !important;
}
</style>
